/**
 * Event phases — mirror of the backend `EVENT_PHASE_KIND` constants.
 *
 * An event's show dates (`start_date` / `end_date`) describe the show only.
 * Setup ("montage") and teardown ("démontage") days live next to the show
 * windows in `event_day_windows`, tagged with their `kind`, and may fall
 * outside the show dates.
 */
export const EVENT_PHASE_KIND = {
  SETUP: "setup",
  SHOW: "show",
  TEARDOWN: "teardown",
} as const;

export type EventPhaseKind =
  (typeof EVENT_PHASE_KIND)[keyof typeof EVENT_PHASE_KIND];

/** Every phase, in chronological order (setup < show < teardown). */
export const EVENT_PHASE_KINDS: readonly EventPhaseKind[] = [
  EVENT_PHASE_KIND.SETUP,
  EVENT_PHASE_KIND.SHOW,
  EVENT_PHASE_KIND.TEARDOWN,
];

/** Phases the organiser adds by hand (show days are derived from the dates). */
export const EVENT_EXTRA_PHASE_KINDS: readonly EventPhaseKind[] = [
  EVENT_PHASE_KIND.SETUP,
  EVENT_PHASE_KIND.TEARDOWN,
];

/** Sort rank of each phase when two windows share the same start. */
export const EVENT_PHASE_ORDER: Record<EventPhaseKind, number> = {
  [EVENT_PHASE_KIND.SETUP]: 0,
  [EVENT_PHASE_KIND.SHOW]: 1,
  [EVENT_PHASE_KIND.TEARDOWN]: 2,
};

/** Maximum number of setup days, and of teardown days, per event. */
export const MAX_PHASE_DAYS = 14;

/** Default show window (night event) used when an event has no saved window. */
export const DEFAULT_SHOW_WINDOW_START = "22:00";
export const DEFAULT_SHOW_WINDOW_END = "06:00";

/** Default window offered for a freshly added setup / teardown day. */
export const DEFAULT_PHASE_WINDOW_START = "08:00";
export const DEFAULT_PHASE_WINDOW_END = "18:00";

/** Separator of a section key: `${section_date}__${kind}`. */
export const SECTION_KEY_SEPARATOR = "__";

/** Client-side validation failures of a day-window list. */
export const DAY_WINDOW_ERROR = {
  OVERLAP: "overlap",
  OUT_OF_PHASE: "out_of_phase",
  TOO_MANY: "too_many",
  SHOW_OUT_OF_RANGE: "show_out_of_range",
  DUPLICATE: "duplicate",
  NO_SHOW: "no_show",
} as const;

export type DayWindowError =
  (typeof DAY_WINDOW_ERROR)[keyof typeof DAY_WINDOW_ERROR];

/** i18n key of each client-side validation failure. */
export const DAY_WINDOW_ERROR_I18N_KEY: Record<DayWindowError, string> = {
  [DAY_WINDOW_ERROR.OVERLAP]: "events.phases.error_overlap",
  [DAY_WINDOW_ERROR.OUT_OF_PHASE]: "events.phases.error_out_of_phase",
  [DAY_WINDOW_ERROR.TOO_MANY]: "events.phases.error_too_many",
  [DAY_WINDOW_ERROR.SHOW_OUT_OF_RANGE]: "events.phases.error_show_out_of_range",
  [DAY_WINDOW_ERROR.DUPLICATE]: "events.phases.error_duplicate",
  [DAY_WINDOW_ERROR.NO_SHOW]: "events.phases.error_no_show",
};

/** i18n key of each phase label ("Montage", "Événement", "Démontage"). */
export const EVENT_PHASE_I18N_KEY: Record<EventPhaseKind, string> = {
  [EVENT_PHASE_KIND.SETUP]: "events.phases.setup",
  [EVENT_PHASE_KIND.SHOW]: "events.phases.show",
  [EVENT_PHASE_KIND.TEARDOWN]: "events.phases.teardown",
};

/** i18n key of each phase's window caption ("Plage de montage", …). */
export const EVENT_PHASE_WINDOW_I18N_KEY: Record<EventPhaseKind, string> = {
  [EVENT_PHASE_KIND.SETUP]: "events.phases.window_setup",
  [EVENT_PHASE_KIND.SHOW]: "events.phases.window_show",
  [EVENT_PHASE_KIND.TEARDOWN]: "events.phases.window_teardown",
};
